import type { ColorEtapa, FlujosPorMateria } from '../types'

export const COLORES_ETAPA: {
  clave: ColorEtapa
  nombre: string
  punto: string
  chip: string
  muestra: string
}[] = [
  {
    clave: 'marino',
    nombre: 'Azul marino',
    punto: 'bg-marino-700',
    chip: 'bg-marino-50 text-marino-700 ring-1 ring-marino-100',
    muestra: 'bg-marino-700',
  },
  {
    clave: 'ambar',
    nombre: 'Ámbar',
    punto: 'bg-ambar-400',
    chip: 'bg-ambar-50 text-ambar-700 ring-1 ring-ambar-100',
    muestra: 'bg-ambar-400',
  },
  {
    clave: 'exito',
    nombre: 'Verde',
    punto: 'bg-exito-500',
    chip: 'bg-exito-50 text-exito-700 ring-1 ring-exito-100',
    muestra: 'bg-exito-500',
  },
  {
    clave: 'peligro',
    nombre: 'Rojo',
    punto: 'bg-peligro-500',
    chip: 'bg-peligro-50 text-peligro-700 ring-1 ring-peligro-100',
    muestra: 'bg-peligro-500',
  },
  {
    clave: 'gris',
    nombre: 'Gris',
    punto: 'bg-gris-400',
    chip: 'bg-gris-100 text-gris-600 ring-1 ring-gris-200',
    muestra: 'bg-gris-400',
  },
]

export function estiloColor(color: ColorEtapa) {
  return COLORES_ETAPA.find((c) => c.clave === color) ?? COLORES_ETAPA[4]
}

/**
 * Flujos precargados como datos de ejemplo. Penal y Laboral vienen configurados a detalle;
 * todos son editables desde Configuración → Flujos de proceso.
 */
const FLUJOS_BASE: FlujosPorMateria = {
  Penal: [
    {
      id: 'fl-pen-1',
      nombre: 'Denuncia',
      descripcion: 'Recepción y ratificación de la denuncia o querella ante el Ministerio Público.',
      tienePlazo: true,
      diasSugeridos: 5,
      documentosEsperados: ['Denuncia', 'Identificación del cliente', 'Poder / carta poder'],
      color: 'marino',
      etiqueta: 'Inicio',
      esHito: false,
    },
    {
      id: 'fl-pen-2',
      nombre: 'Carpeta de investigación',
      descripcion: 'Integración de datos de prueba y actos de investigación de la Fiscalía.',
      tienePlazo: true,
      diasSugeridos: 30,
      documentosEsperados: ['Oficio', 'Peritaje', 'Comprobante de pago'],
      color: 'gris',
      etiqueta: 'Investigación',
      esHito: false,
    },
    {
      id: 'fl-pen-3',
      nombre: 'Audiencia inicial',
      descripcion: 'Control de detención, formulación de imputación y vinculación a proceso.',
      tienePlazo: true,
      diasSugeridos: 15,
      documentosEsperados: ['Promoción', 'Cédula de notificación'],
      color: 'ambar',
      etiqueta: 'Audiencia',
      esHito: true,
    },
    {
      id: 'fl-pen-4',
      nombre: 'Investigación complementaria',
      descripcion: 'Plazo fijado por el juez de control para cerrar la investigación.',
      tienePlazo: true,
      diasSugeridos: 60,
      documentosEsperados: ['Peritaje', 'Oficio', 'Promoción'],
      color: 'gris',
      etiqueta: 'Investigación',
      esHito: false,
    },
    {
      id: 'fl-pen-5',
      nombre: 'Juicio oral',
      descripcion: 'Desahogo de pruebas y alegatos ante el tribunal de enjuiciamiento.',
      tienePlazo: true,
      diasSugeridos: 45,
      documentosEsperados: ['Promoción', 'Peritaje'],
      color: 'ambar',
      etiqueta: 'Audiencia',
      esHito: true,
    },
    {
      id: 'fl-pen-6',
      nombre: 'Sentencia',
      descripcion: 'Explicación de sentencia y cómputo del plazo para apelar.',
      tienePlazo: true,
      diasSugeridos: 10,
      documentosEsperados: ['Sentencia'],
      color: 'exito',
      etiqueta: 'Resolución',
      esHito: true,
    },
  ],
  Laboral: [
    {
      id: 'fl-lab-1',
      nombre: 'Conciliación prejudicial',
      descripcion: 'Instancia obligatoria ante el Centro de Conciliación Laboral.',
      tienePlazo: true,
      diasSugeridos: 45,
      documentosEsperados: ['Escrito inicial', 'Identificación'],
      color: 'marino',
      etiqueta: 'Prejudicial',
      esHito: false,
    },
    {
      id: 'fl-lab-2',
      nombre: 'Demanda',
      descripcion: 'Presentación de la demanda ante el Tribunal Laboral con constancia de no conciliación.',
      tienePlazo: true,
      diasSugeridos: 15,
      documentosEsperados: ['Escrito inicial', 'Poder notarial', 'Contrato'],
      color: 'marino',
      etiqueta: 'Inicio',
      esHito: false,
    },
    {
      id: 'fl-lab-3',
      nombre: 'Audiencia preliminar',
      descripcion: 'Fijación de la litis, ofrecimiento y admisión de pruebas.',
      tienePlazo: true,
      diasSugeridos: 20,
      documentosEsperados: ['Promoción', 'Cédula de notificación'],
      color: 'ambar',
      etiqueta: 'Audiencia',
      esHito: true,
    },
    {
      id: 'fl-lab-4',
      nombre: 'Audiencia de juicio',
      descripcion: 'Desahogo de pruebas admitidas y alegatos de las partes.',
      tienePlazo: true,
      diasSugeridos: 30,
      documentosEsperados: ['Peritaje', 'Promoción'],
      color: 'ambar',
      etiqueta: 'Audiencia',
      esHito: true,
    },
    {
      id: 'fl-lab-5',
      nombre: 'Laudo',
      descripcion: 'Resolución del tribunal y cómputo del plazo para amparo directo.',
      tienePlazo: true,
      diasSugeridos: 15,
      documentosEsperados: ['Laudo'],
      color: 'exito',
      etiqueta: 'Resolución',
      esHito: true,
    },
  ],
  Civil: [
    {
      id: 'fl-civ-1',
      nombre: 'Demanda',
      descripcion: 'Escrito inicial con documentos base de la acción.',
      tienePlazo: true,
      diasSugeridos: 10,
      documentosEsperados: ['Escrito inicial', 'Poder notarial'],
      color: 'marino',
      etiqueta: 'Inicio',
      esHito: false,
    },
    {
      id: 'fl-civ-2',
      nombre: 'Emplazamiento',
      descripcion: 'Notificación personal a la parte demandada.',
      tienePlazo: true,
      diasSugeridos: 20,
      documentosEsperados: ['Cédula de notificación'],
      color: 'gris',
      etiqueta: 'Trámite',
      esHito: false,
    },
    {
      id: 'fl-civ-3',
      nombre: 'Contestación',
      descripcion: 'Contestación de la demanda y excepciones.',
      tienePlazo: true,
      diasSugeridos: 9,
      documentosEsperados: ['Promoción'],
      color: 'gris',
      etiqueta: 'Trámite',
      esHito: false,
    },
    {
      id: 'fl-civ-4',
      nombre: 'Ofrecimiento de pruebas',
      descripcion: 'Ofrecimiento y admisión de pruebas de ambas partes.',
      tienePlazo: true,
      diasSugeridos: 10,
      documentosEsperados: ['Promoción', 'Peritaje'],
      color: 'gris',
      etiqueta: 'Pruebas',
      esHito: false,
    },
    {
      id: 'fl-civ-5',
      nombre: 'Audiencia de desahogo',
      descripcion: 'Desahogo de pruebas admitidas.',
      tienePlazo: false,
      diasSugeridos: 0,
      documentosEsperados: ['Promoción'],
      color: 'ambar',
      etiqueta: 'Audiencia',
      esHito: true,
    },
    {
      id: 'fl-civ-6',
      nombre: 'Alegatos',
      descripcion: 'Alegatos de buena prueba previos a citar para sentencia.',
      tienePlazo: true,
      diasSugeridos: 5,
      documentosEsperados: ['Promoción'],
      color: 'gris',
      etiqueta: 'Trámite',
      esHito: false,
    },
    {
      id: 'fl-civ-7',
      nombre: 'Sentencia',
      descripcion: 'Sentencia definitiva y plazo para apelación.',
      tienePlazo: true,
      diasSugeridos: 9,
      documentosEsperados: ['Acuerdo'],
      color: 'exito',
      etiqueta: 'Resolución',
      esHito: true,
    },
  ],
  Mercantil: [
    {
      id: 'fl-mer-1',
      nombre: 'Demanda ejecutiva',
      descripcion: 'Demanda con títulos de crédito originales.',
      tienePlazo: true,
      diasSugeridos: 10,
      documentosEsperados: ['Escrito inicial', 'Contrato'],
      color: 'marino',
      etiqueta: 'Inicio',
      esHito: false,
    },
    {
      id: 'fl-mer-2',
      nombre: 'Auto de exequendo',
      descripcion: 'Auto que ordena requerir el pago y, en su defecto, embargar.',
      tienePlazo: false,
      diasSugeridos: 0,
      documentosEsperados: ['Acuerdo'],
      color: 'gris',
      etiqueta: 'Trámite',
      esHito: false,
    },
    {
      id: 'fl-mer-3',
      nombre: 'Embargo',
      descripcion: 'Diligencia de embargo y designación de depositario.',
      tienePlazo: true,
      diasSugeridos: 15,
      documentosEsperados: ['Oficio', 'Cédula de notificación'],
      color: 'ambar',
      etiqueta: 'Diligencia',
      esHito: true,
    },
    {
      id: 'fl-mer-4',
      nombre: 'Contestación',
      descripcion: 'Contestación y oposición de excepciones del demandado.',
      tienePlazo: true,
      diasSugeridos: 8,
      documentosEsperados: ['Promoción'],
      color: 'gris',
      etiqueta: 'Trámite',
      esHito: false,
    },
    {
      id: 'fl-mer-5',
      nombre: 'Pruebas',
      descripcion: 'Ofrecimiento y desahogo de pruebas.',
      tienePlazo: true,
      diasSugeridos: 15,
      documentosEsperados: ['Promoción', 'Peritaje'],
      color: 'gris',
      etiqueta: 'Pruebas',
      esHito: false,
    },
    {
      id: 'fl-mer-6',
      nombre: 'Sentencia',
      descripcion: 'Sentencia y ejecución forzosa del adeudo.',
      tienePlazo: true,
      diasSugeridos: 9,
      documentosEsperados: ['Acuerdo'],
      color: 'exito',
      etiqueta: 'Resolución',
      esHito: true,
    },
  ],
  Familiar: [
    {
      id: 'fl-fam-1',
      nombre: 'Solicitud',
      descripcion: 'Solicitud o demanda inicial ante el juzgado familiar.',
      tienePlazo: true,
      diasSugeridos: 10,
      documentosEsperados: ['Escrito inicial', 'Identificación'],
      color: 'marino',
      etiqueta: 'Inicio',
      esHito: false,
    },
    {
      id: 'fl-fam-2',
      nombre: 'Admisión',
      descripcion: 'Auto admisorio y señalamiento de medidas.',
      tienePlazo: false,
      diasSugeridos: 0,
      documentosEsperados: ['Acuerdo'],
      color: 'gris',
      etiqueta: 'Trámite',
      esHito: false,
    },
    {
      id: 'fl-fam-3',
      nombre: 'Medidas provisionales',
      descripcion: 'Pensión provisional, guarda y régimen de convivencias.',
      tienePlazo: true,
      diasSugeridos: 15,
      documentosEsperados: ['Promoción', 'Oficio'],
      color: 'ambar',
      etiqueta: 'Medidas',
      esHito: false,
    },
    {
      id: 'fl-fam-4',
      nombre: 'Audiencia',
      descripcion: 'Audiencia de pruebas y alegatos.',
      tienePlazo: false,
      diasSugeridos: 0,
      documentosEsperados: ['Promoción', 'Peritaje'],
      color: 'ambar',
      etiqueta: 'Audiencia',
      esHito: true,
    },
    {
      id: 'fl-fam-5',
      nombre: 'Sentencia',
      descripcion: 'Sentencia definitiva y ejecución.',
      tienePlazo: true,
      diasSugeridos: 9,
      documentosEsperados: ['Acuerdo'],
      color: 'exito',
      etiqueta: 'Resolución',
      esHito: true,
    },
  ],
  Administrativo: [
    {
      id: 'fl-adm-1',
      nombre: 'Recurso administrativo',
      descripcion: 'Recurso de revocación o inconformidad ante la propia autoridad.',
      tienePlazo: true,
      diasSugeridos: 30,
      documentosEsperados: ['Escrito inicial', 'Comprobante de pago'],
      color: 'marino',
      etiqueta: 'Inicio',
      esHito: false,
    },
    {
      id: 'fl-adm-2',
      nombre: 'Demanda de nulidad',
      descripcion: 'Juicio contencioso administrativo ante el tribunal competente.',
      tienePlazo: true,
      diasSugeridos: 30,
      documentosEsperados: ['Escrito inicial', 'Poder notarial'],
      color: 'marino',
      etiqueta: 'Inicio',
      esHito: false,
    },
    {
      id: 'fl-adm-3',
      nombre: 'Contestación de la autoridad',
      descripcion: 'Contestación y exhibición del expediente administrativo.',
      tienePlazo: true,
      diasSugeridos: 30,
      documentosEsperados: ['Oficio'],
      color: 'gris',
      etiqueta: 'Trámite',
      esHito: false,
    },
    {
      id: 'fl-adm-4',
      nombre: 'Cierre de instrucción',
      descripcion: 'Alegatos y cierre de instrucción.',
      tienePlazo: true,
      diasSugeridos: 5,
      documentosEsperados: ['Promoción'],
      color: 'ambar',
      etiqueta: 'Alegatos',
      esHito: true,
    },
    {
      id: 'fl-adm-5',
      nombre: 'Sentencia',
      descripcion: 'Sentencia de la sala y cumplimiento.',
      tienePlazo: true,
      diasSugeridos: 15,
      documentosEsperados: ['Acuerdo'],
      color: 'exito',
      etiqueta: 'Resolución',
      esHito: true,
    },
  ],
  Amparo: [
    {
      id: 'fl-amp-1',
      nombre: 'Demanda de amparo',
      descripcion: 'Presentación de la demanda con señalamiento del acto reclamado.',
      tienePlazo: true,
      diasSugeridos: 15,
      documentosEsperados: ['Escrito inicial', 'Identificación'],
      color: 'marino',
      etiqueta: 'Inicio',
      esHito: false,
    },
    {
      id: 'fl-amp-2',
      nombre: 'Admisión',
      descripcion: 'Auto admisorio y requerimiento de informes.',
      tienePlazo: false,
      diasSugeridos: 0,
      documentosEsperados: ['Acuerdo'],
      color: 'gris',
      etiqueta: 'Trámite',
      esHito: false,
    },
    {
      id: 'fl-amp-3',
      nombre: 'Suspensión',
      descripcion: 'Incidente de suspensión provisional y definitiva.',
      tienePlazo: true,
      diasSugeridos: 10,
      documentosEsperados: ['Promoción', 'Comprobante de pago'],
      color: 'peligro',
      etiqueta: 'Urgente',
      esHito: true,
    },
    {
      id: 'fl-amp-4',
      nombre: 'Informe justificado',
      descripcion: 'Vista del informe de la autoridad responsable.',
      tienePlazo: true,
      diasSugeridos: 8,
      documentosEsperados: ['Oficio'],
      color: 'gris',
      etiqueta: 'Trámite',
      esHito: false,
    },
    {
      id: 'fl-amp-5',
      nombre: 'Audiencia constitucional',
      descripcion: 'Celebración de la audiencia constitucional.',
      tienePlazo: false,
      diasSugeridos: 0,
      documentosEsperados: ['Promoción'],
      color: 'ambar',
      etiqueta: 'Audiencia',
      esHito: true,
    },
    {
      id: 'fl-amp-6',
      nombre: 'Sentencia',
      descripcion: 'Sentencia de amparo y cumplimiento.',
      tienePlazo: true,
      diasSugeridos: 10,
      documentosEsperados: ['Sentencia'],
      color: 'exito',
      etiqueta: 'Resolución',
      esHito: true,
    },
  ],
}

/** Equivalencias con los nombres del catálogo de documentos vigente. */
const ALIAS_DOCUMENTO: Record<string, string> = {
  'Escrito inicial': 'Demanda',
  'Poder notarial': 'Poder / carta poder',
  Identificación: 'Identificación del cliente',
  Peritaje: 'Prueba pericial',
  'Cédula de notificación': 'Acuse',
  Contrato: 'Contrato de prestación de servicios',
  'Comprobante de pago': 'Factura',
}

export const FLUJOS_INICIALES: FlujosPorMateria = Object.fromEntries(
  Object.entries(FLUJOS_BASE).map(([materia, etapas]) => [
    materia,
    etapas.map((e) => ({
      ...e,
      documentosEsperados: [
        ...new Set(e.documentosEsperados.map((d) => ALIAS_DOCUMENTO[d] ?? d)),
      ],
    })),
  ]),
) as FlujosPorMateria
